import { type DragEvent, useCallback, useMemo, useRef, useState } from 'react'
import { hasDraggedFiles } from './file-drag.js'

type Options = {
  onHoverStart?: () => void
  onHoverEnd?: () => void
}

type FileDragHoverHandlers = {
  onDragEnter: (e: DragEvent<HTMLElement>) => void
  onDragLeave: (e: DragEvent<HTMLElement>) => void
  onDrop: (e: DragEvent<HTMLElement>) => void
}

/**
 * 要素の上にファイルがドラッグされているかを追う。
 *
 * dragenter / dragleave は子要素の境界を越えるたびに発火するので、出入りの回数で判定する。
 * dragleave の relatedTarget で判定する方式は Safari で値が取れず、強調がちらつくため使わない。
 * ドロップ時は dragleave が来ないので onDrop でも戻す。
 */
export function useFileDragHover(options: Options = {}): {
  isOver: boolean
  handlers: FileDragHoverHandlers
} {
  const [isOver, setIsOver] = useState(false)
  const depth = useRef(0)
  const optionsRef = useRef(options)
  optionsRef.current = options

  const end = useCallback(() => {
    depth.current = 0
    setIsOver(false)
    optionsRef.current.onHoverEnd?.()
  }, [])

  const handlers = useMemo<FileDragHoverHandlers>(
    () => ({
      onDragEnter: (e) => {
        if (!hasDraggedFiles(e.dataTransfer)) return
        depth.current += 1
        if (depth.current === 1) {
          setIsOver(true)
          optionsRef.current.onHoverStart?.()
        }
      },
      onDragLeave: (e) => {
        if (!hasDraggedFiles(e.dataTransfer)) return
        depth.current = Math.max(0, depth.current - 1)
        if (depth.current === 0) end()
      },
      onDrop: () => end(),
    }),
    [end],
  )

  return { isOver, handlers }
}
